import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Users2, GraduationCap, ClipboardCheck, Sparkles, Shield, TrendingUp, Layers } from "lucide-react";
import { Button } from "../components/ui/button";
import { PublicNav, PublicFooter } from "../components/PublicChrome";

const AUDIENCE = [
    { Icon: ClipboardCheck, title: "District Leaders", body: "Get a single executive dashboard showing weighted health across every domain, plus board-ready PDF reports." },
    { Icon: Users2, title: "School Coaches & Reviewers", body: "Score rubrics, capture evidence, and log findings without the spreadsheet chaos." },
    { Icon: GraduationCap, title: "Teachers", body: "Reflect on your practice, receive coaching-aligned feedback, and see your own growth in the Instructional Core." },
];

const VALUES = [
    { Icon: Shield, label: "Evidence over opinion" },
    { Icon: Layers, label: "Coherence over compliance" },
    { Icon: TrendingUp, label: "Growth over grading" },
    { Icon: Sparkles, label: "Systems over silos" },
];

function TextBlock({ eyebrow, heading, children, testid }) {
    return (
        <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
            data-testid={testid}
        >
            {eyebrow && (
                <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">{eyebrow}</div>
            )}
            {heading && (
                <h2 className="font-serif text-3xl sm:text-4xl text-[#0B2545] font-semibold leading-tight mb-6">
                    {heading}
                </h2>
            )}
            <div className="text-[#0E1B2C]/85 text-lg leading-relaxed">{children}</div>
        </motion.section>
    );
}

export default function AboutPage() {
    return (
        <div className="min-h-screen bg-white text-[#0E1B2C]" data-testid="about-page">
            <PublicNav variant="dark" />

            <section
                className="pt-32 pb-16 lg:pt-40 lg:pb-24 px-6 lg:px-10 text-white overflow-hidden relative"
                style={{ background: "linear-gradient(135deg, #0B2545 0%, #1E56A0 60%, #8AC4FF 120%)" }}
                data-testid="about-hero"
            >
                <motion.div
                    aria-hidden
                    className="absolute -top-24 -left-24 h-[420px] w-[420px] rounded-full bg-[#8AC4FF]/25 blur-3xl"
                    animate={{ y: [0, 20, 0] }}
                    transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                />
                <div className="max-w-7xl mx-auto relative">
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8AC4FF] mb-3">About ESSS</div>
                    <motion.h1
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight max-w-3xl"
                        data-testid="about-title"
                    >
                        Organizational coherence, made observable.
                    </motion.h1>
                    <p className="mt-6 max-w-2xl text-lg text-white/85 leading-relaxed">
                        We help school systems diagnose alignment, execute with discipline, and measure what matters — using one integrated framework.
                    </p>
                </div>
            </section>

            <section className="py-20 lg:py-24 px-6 lg:px-10">
                <div className="max-w-7xl mx-auto space-y-20">
                    <TextBlock eyebrow="Our Mission" heading="Every education leader deserves a clear picture." testid="about-mission">
                        <p>
                            Every education leader deserves a clear picture of how the parts of their organization actually work together. The ESSS Coherence Platform turns a proven six-domain framework into a working system — replacing scattered audits, one-off surveys, and shelf-bound strategic plans with a single source of truth that districts, schools, coaches, and teachers can all see and act on.
                        </p>
                    </TextBlock>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <TextBlock eyebrow="The Framework" heading="Six interdependent domains." testid="about-framework">
                            <p>
                                The ESSS Organizational Coherence Model™ evaluates six interdependent domains that define whether a school system operates as a coherent whole or as a set of disconnected initiatives:
                                Vision & Strategic Coherence, Leadership & Systems Execution, Culture & Climate, Curriculum & Assessment, Instructional Core, and Data Systems & Progress Monitoring.
                                Each is scored against a four-level rubric that moves an organization from <em>Minimal/Fragmented</em> to <em>Highly Coherent/Adaptive</em>.
                            </p>
                        </TextBlock>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.94 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.6 }}
                            className="relative rounded-3xl overflow-hidden shadow-2xl shadow-[#0B2545]/20"
                        >
                            <img
                                src="/widget-data-coherence.jpg"
                                alt="Interconnected data nodes representing organizational coherence"
                                className="w-full h-full object-cover"
                                style={{ minHeight: 320 }}
                            />
                        </motion.div>
                    </div>

                    <div>
                        <div className="max-w-2xl mb-10">
                            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">Who It's For</div>
                            <h2 className="font-serif text-3xl sm:text-4xl text-[#0B2545] font-semibold leading-tight">
                                Built for every seat in the district.
                            </h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                            {AUDIENCE.map(({ Icon, title, body }, i) => (
                                <motion.div
                                    key={title}
                                    initial={{ opacity: 0, y: 18 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    className="rounded-2xl border border-[#D6E6F2] bg-white p-6 lg:p-8 hover:border-[#8AC4FF] hover:shadow-xl hover:shadow-[#0B2545]/5 transition-all"
                                    data-testid={`about-audience-${i}`}
                                >
                                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#0B2545] text-white mb-5">
                                        <Icon size={22} />
                                    </div>
                                    <h3 className="font-serif text-xl text-[#0B2545] font-semibold mb-2">{title}</h3>
                                    <p className="text-[#0E1B2C]/80 leading-relaxed">{body}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.94 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.6 }}
                            className="relative rounded-3xl overflow-hidden shadow-2xl shadow-[#0B2545]/20 order-2 lg:order-1"
                        >
                            <img
                                src="/widget-students.jpg"
                                alt="Students collaborating in a classroom"
                                className="w-full h-full object-cover"
                                style={{ minHeight: 320 }}
                            />
                        </motion.div>
                        <div className="order-1 lg:order-2">
                            <TextBlock eyebrow="Our Approach" heading="Evidence-first. Audit-defensible. Consistent across every school." testid="about-approach">
                                <p>
                                    We built ESSS to be evidence-first and audit-defensible. Every rating is tied to observable look-fors. Every finding, red flag, and recommendation is timestamped in the audit log. Every cycle can be locked and exported as a signed report. The framework does not change based on who is looking at it — the same six domains, the same rubric language, applied consistently across every school.
                                </p>
                            </TextBlock>
                        </div>
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">What we stand for</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {VALUES.map(({ Icon, label }, i) => (
                                <motion.div
                                    key={label}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: i * 0.06 }}
                                    className="rounded-2xl border border-[#D6E6F2] bg-[#F1F6FB] p-5 text-center"
                                    data-testid={`about-value-${i}`}
                                >
                                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-white text-[#0B2545] mb-3 shadow">
                                        <Icon size={18} />
                                    </div>
                                    <div className="font-serif text-base font-semibold text-[#0B2545]">{label}</div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-[#0B2545] text-white py-20 px-6 lg:px-10">
                <div className="max-w-4xl mx-auto text-left lg:text-center">
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
                        Ready to see your organization's coherence?
                    </h2>
                    <div className="mt-8 flex flex-wrap gap-4 lg:justify-center">
                        <Link to="/login" data-testid="about-cta-signin">
                            <Button size="lg" className="rounded-full h-12 px-7 bg-white text-[#0B2545] hover:bg-[#D6E6F2] font-semibold">
                                Sign In
                                <ArrowRight className="ml-1" size={18} />
                            </Button>
                        </Link>
                        <Link to="/contact" data-testid="about-cta-contact">
                            <Button size="lg" variant="outline" className="rounded-full h-12 px-7 border-white/40 text-white bg-transparent hover:bg-white/10">
                                Contact Us
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
}
